import { useMemo } from 'react';
import dayjs from 'dayjs';
import type { TimeSlot, VisitBlock, PreviewVisit, HolidayBlock } from './entity';
import type { VisitDetail } from '../../entity';
import type { WorkingHoursScheduleType } from '@features/visits/schedule/entity';
import { getVisitColor } from './colorUtils';

const SLOT_HEIGHT = 60; // pixels per 30 minutes

export const useDayTimeline = (
  date: Date,
  visits: VisitDetail[] | undefined,
  workingHours?: { start: string; end: string },
  previewVisit?: PreviewVisit,
  workingSchedule?: WorkingHoursScheduleType,
) => {
  // Generate time slots based on working hours and actual visits
  const timeSlots = useMemo((): TimeSlot[] => {
    const slots: TimeSlot[] = [];

    let startHour = workingHours?.start ? parseInt(workingHours.start.split(':')[0]) : 8;
    let endHour = workingHours?.end ? parseInt(workingHours.end.split(':')[0]) : 18;

    if (visits?.length) {
      const todayVisits = visits.filter(
        ({ visit }) =>
          dayjs(visit.dateFrom).format('YYYY-MM-DD') === dayjs(date).format('YYYY-MM-DD'),
      );

      todayVisits.forEach(({ visit }) => {
        const visitStartHour = dayjs(visit.dateFrom).hour();
        const visitEndHour = dayjs(visit.dateTo).hour();
        const visitEndMinutes = dayjs(visit.dateTo).minute();

        // Extend start if visit starts earlier
        if (visitStartHour < startHour) {
          startHour = visitStartHour;
        }

        // Extend end if visit ends later (round up to next hour if there are minutes)
        const visitEndCeiling = visitEndMinutes > 0 ? visitEndHour + 1 : visitEndHour;
        if (visitEndCeiling > endHour) {
          endHour = visitEndCeiling;
        }
      });
    }

    // Generate slots from adjusted start to end
    for (let hour = startHour; hour <= endHour; hour++) {
      slots.push({
        time: `${String(hour).padStart(2, '0')}:00`,
        label: `${String(hour).padStart(2, '0')}:00`,
      });
      if (hour < endHour) {
        slots.push({
          time: `${String(hour).padStart(2, '0')}:30`,
          label: `${String(hour).padStart(2, '0')}:30`,
        });
      }
    }

    return slots;
  }, [workingHours, visits, date]);

  // Convert time to minutes from start of day
  const timeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Calculate position in pixels
  const getPosition = (time: string): number => {
    const startMinutes = timeToMinutes(timeSlots[0]?.time || '08:00');
    const currentMinutes = timeToMinutes(time);
    const minutesFromStart = currentMinutes - startMinutes;
    return (minutesFromStart / 30) * SLOT_HEIGHT;
  };

  // Convert visits to blocks with positions and handle overlaps
  const visitBlocks = useMemo((): VisitBlock[] => {
    if (!visits?.length) {
      return [];
    }

    const blocks: VisitBlock[] = visits
      .filter(
        ({ visit }) =>
          dayjs(visit.dateFrom).format('YYYY-MM-DD') === dayjs(date).format('YYYY-MM-DD'),
      )
      .map(({ visit, services, client }, index) => {
        const startTime = dayjs(visit.dateFrom).format('HH:mm');
        const endTime = dayjs(visit.dateTo).format('HH:mm');
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);
        const duration = endMinutes - startMinutes;

        const serviceName = services?.[0]?.serviceName;

        return {
          id: visit.id,
          startTime,
          endTime,
          clientName: client ? `${client.firstName} ${client.lastName}` : undefined,
          serviceName,
          top: getPosition(startTime),
          height: (duration / 30) * SLOT_HEIGHT,
          color: getVisitColor(index, serviceName),
          startMinutes,
          endMinutes,
          column: 0,
          totalColumns: 1,
        };
      })
      .sort((a, b) => (a.startMinutes || 0) - (b.startMinutes || 0));

    // Detect overlaps and assign columns
    const columns: VisitBlock[][] = [];

    blocks.forEach(block => {
      let placed = false;

      // Try to place in existing column
      for (let i = 0; i < columns.length; i++) {
        const column = columns[i];
        const lastInColumn = column[column.length - 1];

        // Check if this block overlaps with the last block in this column
        if ((block.startMinutes || 0) >= (lastInColumn.endMinutes || 0)) {
          column.push(block);
          block.column = i;
          placed = true;
          break;
        }
      }

      // If not placed, create new column
      if (!placed) {
        columns.push([block]);
        block.column = columns.length - 1;
      }
    });

    // Calculate max columns for each block
    blocks.forEach(block => {
      let maxColumns = 1;

      // Find all blocks that overlap with this one
      blocks.forEach(other => {
        if (
          block.id !== other.id &&
          (block.startMinutes || 0) < (other.endMinutes || 0) &&
          (block.endMinutes || 0) > (other.startMinutes || 0)
        ) {
          maxColumns = Math.max(maxColumns, columns.length);
        }
      });

      block.totalColumns = maxColumns;
    });

    return blocks;
  }, [visits, date, timeSlots]);

  // Convert preview visit to block
  const previewBlock = useMemo((): VisitBlock | undefined => {
    if (!previewVisit) {
      return undefined;
    }

    const startMinutes = timeToMinutes(previewVisit.startTime);
    const endMinutes = timeToMinutes(previewVisit.endTime);
    const duration = endMinutes - startMinutes;

    return {
      id: 'preview',
      startTime: previewVisit.startTime,
      endTime: previewVisit.endTime,
      serviceName: previewVisit.serviceName,
      top: getPosition(previewVisit.startTime),
      height: (duration / 30) * SLOT_HEIGHT,
      color: 'rgba(49, 116, 173, 0.5)',
    };
  }, [previewVisit, timeSlots]);

  // Convert holidays to blocks
  const holidayBlocks = useMemo((): HolidayBlock[] => {
    if (!workingSchedule?.holidays) {
      return [];
    }

    const targetDateStr = dayjs(date).format('YYYY-MM-DD');
    const matchingHolidays = workingSchedule.holidays.filter(h => {
      return h.date === targetDateStr;
    });

    return matchingHolidays
      .filter(h => h.startTime && h.endTime) // Only show time-based holidays
      .map((holiday, index) => {
        const startMinutes = timeToMinutes(holiday.startTime!);
        const endMinutes = timeToMinutes(holiday.endTime!);
        const duration = endMinutes - startMinutes;

        return {
          id: `holiday-${index}`,
          startTime: holiday.startTime!,
          endTime: holiday.endTime!,
          name: holiday.name,
          top: getPosition(holiday.startTime!),
          height: (duration / 30) * SLOT_HEIGHT,
        };
      });
  }, [workingSchedule, date, timeSlots]);

  return {
    timeSlots,
    visitBlocks,
    previewBlock,
    holidayBlocks,
    slotHeight: SLOT_HEIGHT,
  };
};
